/**
 * TinyQueue - A lightweight, type-safe priority queue implementation
 */

export type CompareFunction<T> = (a: T, b: T) => number

export class TinyQueue<T> {
  data: T[]
  private length: number
  private compare: CompareFunction<T>

  constructor(data: T[] = [], compare: CompareFunction<T> = defaultCompare) {
    this.data = data.slice()
    this.length = this.data.length
    this.compare = compare

    if (this.length > 0) {
      for (let i = (this.length >> 1) - 1; i >= 0; i--) {
        this._down(i)
      }
    }
  }

  /**
   * Returns the number of items in the queue
   */
  get size(): number {
    return this.length
  }

  /**
   * Pushes an item into the queue
   */
  push(item: T): void {
    this.data[this.length] = item
    this._up(this.length++)
  }

  /**
   * Returns the top item without removing it
   */
  peek(): T | undefined {
    if (this.length === 0) return undefined
    return this.data[0]
  }

  /**
   * Removes and returns the top item
   */
  pop(): T | undefined {
    if (this.length === 0) return undefined

    const top = this.data[0]
    this.length--

    if (this.length > 0) {
      this.data[0] = this.data[this.length]!
      this._down(0)
    }

    return top
  }

  /**
   * Removes all items from the queue
   */
  clear(): void {
    this.data.length = 0
    this.length = 0
  }

  /**
   * Push an item to the correct position in the heap based on priority
   */
  private _up(pos: number): void {
    const { data, compare } = this
    const item = data[pos]!

    while (pos > 0) {
      const parent = (pos - 1) >> 1
      const current = data[parent]!
      if (compare(item, current) >= 0) break
      data[pos] = current
      pos = parent
    }

    data[pos] = item
  }

  /**
   * Sink an item to the correct position in the heap based on priority
   */
  private _down(pos: number): void {
    const { data, compare } = this
    const halfLength = this.length >> 1
    const item = data[pos]!

    while (pos < halfLength) {
      let bestIndex = (pos << 1) + 1
      const rightIndex = bestIndex + 1

      if (
        rightIndex < this.length &&
        compare(data[rightIndex]!, data[bestIndex]!) < 0
      ) {
        bestIndex = rightIndex
      }

      if (compare(data[bestIndex]!, item) >= 0) break

      data[pos] = data[bestIndex]!
      pos = bestIndex
    }

    data[pos] = item
  }
}

/**
 * Default comparison function
 */
function defaultCompare<T>(a: T, b: T): number {
  return (a as any) < (b as any) ? -1 : (a as any) > (b as any) ? 1 : 0
}
